import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Slider,
  Alert,
  Fade,
  InputAdornment,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { AccessTime, Tag, Note, Mood, Category, PlayArrow, Pause, Stop, Schedule, Save, Close } from '@mui/icons-material';
import { updateCareRecord } from '../services/firestoreService';
import { toast } from 'react-toastify';
import { IconAvatar } from './IconSystem';
import { format } from 'date-fns';

const FEELING_OPTIONS = [
  '罪悪感',
  '虚無感',
  '特になし',
  '満足',
  '前向き',
  'その他'
];

const CONTENT_TYPE_OPTIONS = [
  '音声',
  '映像',
  '画像',
  '文章',
  'ツール',
  'パートナー',
  '妄想',
  'なし',
  'その他'
];

export default function EditRecordForm({ record, open, onClose, onRecordUpdated }) {
  const [duration, setDuration] = useState(0);
  const [skipDuration, setSkipDuration] = useState(false);
  const [feeling, setFeeling] = useState('');
  const [customFeeling, setCustomFeeling] = useState('');
  const [contentType, setContentType] = useState('');
  const [tags, setTags] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // タイマー関連の状態
  const [useTimer, setUseTimer] = useState(false);
  const [timerDuration, setTimerDuration] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  
  // 日時指定関連の状態
  const [customDateTime, setCustomDateTime] = useState('');
  const [useCustomDateTime, setUseCustomDateTime] = useState(false);
  const [onlyDate, setOnlyDate] = useState(false);

  // 記録データを初期値として設定
  useEffect(() => {
    if (record && open) {
      // 所要時間の設定
      if (record.duration) {
        setDuration(Math.floor(record.duration / 60));
        setSkipDuration(false);
      } else {
        setDuration(0);
        setSkipDuration(true);
      }
      
      // 気分・感情の設定
      if (FEELING_OPTIONS.includes(record.feeling)) {
        setFeeling(record.feeling);
        setCustomFeeling('');
      } else {
        setFeeling('その他');
        setCustomFeeling(record.feeling);
      }
      
      setContentType(record.content_type || '');
      setTags(record.tags ? record.tags.join(', ') : '');
      setNote(record.note || '');
      
      // 日時の設定
      if (record.timeUnknown) {
        setUseCustomDateTime(true);
        setOnlyDate(true);
        setCustomDateTime(format(record.timestamp, 'yyyy-MM-dd'));
      } else {
        const now = new Date();
        const recordTime = new Date(record.timestamp);
        // 現在時刻から5分以上離れている場合は手動指定とみなす
        if (Math.abs(now - recordTime) > 5 * 60 * 1000) {
          setUseCustomDateTime(true);
          setOnlyDate(false);
          setCustomDateTime(format(record.timestamp, "yyyy-MM-dd'T'HH:mm"));
        } else {
          setUseCustomDateTime(false);
          setOnlyDate(false);
          setCustomDateTime('');
        }
      }
      
      setUseTimer(false);
      setTimerDuration(0);
      setIsTimerRunning(false);
    }
  }, [record, open]);

  // タイマー用のuseEffect
  useEffect(() => {
    let interval = null;
    if (isTimerRunning) {
      interval = setInterval(() => {
        setTimerDuration(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isTimerRunning]);

  const startTimer = () => {
    setIsTimerRunning(true);
    if (timerDuration === 0) {
      setTimerDuration(0);
    }
  };

  const pauseTimer = () => {
    setIsTimerRunning(false);
  };

  const stopTimer = () => {
    setIsTimerRunning(false);
    if (timerDuration > 0) {
      const minutes = Math.ceil(timerDuration / 60);
      setDuration(minutes);
    }
    setTimerDuration(0);
  };

  const formatTimerDisplay = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const finalFeeling = feeling === 'その他' ? customFeeling : feeling;
      const tagArray = tags.split(',').map(tag => tag.trim()).filter(tag => tag);

      const recordData = {
        feeling: finalFeeling,
        content_type: contentType,
        tags: tagArray,
        note: note
      };

      // 所要時間が指定されている場合のみ追加
      if (!skipDuration) {
        if (useTimer && timerDuration > 0) {
          recordData.duration = timerDuration;
        } else if (!useTimer && duration > 0) {
          recordData.duration = duration * 60; // 分を秒に変換
        } else {
          recordData.duration = 0; // デフォルト値
        }
      }

      // カスタム日時が指定されている場合は追加
      if (useCustomDateTime && customDateTime) {
        if (onlyDate) {
          // 日付のみの場合は時刻情報なしフラグを追加
          const dateOnly = new Date(customDateTime + 'T00:00:00');
          recordData.customTimestamp = dateOnly;
          recordData.timeUnknown = true; // 時刻不明フラグ
        } else {
          recordData.customTimestamp = new Date(customDateTime);
          recordData.timeUnknown = false;
        }
      }

      await updateCareRecord(record.id, recordData);
      
      toast.success('記録を更新しました！', {
        position: "top-right",
        autoClose: 3000,
      });
      
      if (onRecordUpdated) {
        onRecordUpdated();
      }
      
      onClose();
    } catch (error) {
      setError('記録の更新に失敗しました: ' + error.message);
    }
    
    setLoading(false);
  };

  const handleClose = () => {
    setIsTimerRunning(false);
    setError('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          borderRadius: '20px',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box display="flex" alignItems="center">
          <IconAvatar type="feeling" value={feeling || "前向き"} size={32} />
          <Typography variant="h6" sx={{ ml: 2, fontWeight: 700 }}>
            記録を編集
          </Typography>
        </Box>
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{error}</Alert>}
        
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* 所要時間 */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" mb={2}>
                <AccessTime sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  所要時間: {skipDuration ? '記録しない' : (useTimer ? formatTimerDisplay(timerDuration) : duration > 0 ? `${duration}分` : '未設定')}
                </Typography>
              </Box>

              {/* 所要時間をスキップするオプション */}
              <FormControlLabel
                control={
                  <Switch
                    checked={skipDuration}
                    onChange={(e) => {
                      setSkipDuration(e.target.checked);
                      if (e.target.checked) {
                        setUseTimer(false);
                        setIsTimerRunning(false);
                        setTimerDuration(0);
                      }
                    }}
                  />
                }
                label="所要時間を記録しない"
                sx={{ mb: 2 }}
              />
              
              {/* タイマー使用切り替え */}
              {!skipDuration && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={useTimer}
                      onChange={(e) => {
                        setUseTimer(e.target.checked);
                        if (!e.target.checked) {
                          setIsTimerRunning(false);
                          setTimerDuration(0);
                        }
                      }}
                    />
                  }
                  label="タイマーを使用"
                  sx={{ mb: 2 }}
                />
              )}
              
              {!skipDuration && useTimer ? (
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    {formatTimerDisplay(timerDuration)}
                  </Typography>
                  <Box display="flex" gap={1}>
                    <Tooltip title={isTimerRunning ? "一時停止" : "開始"}>
                      <IconButton
                        onClick={isTimerRunning ? pauseTimer : startTimer}
                        color="primary"
                        sx={{
                          background: 'linear-gradient(135deg, #66BB6A, #42A5F5)',
                          color: 'white',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #4CAF50, #1976D2)',
                            transform: 'scale(1.1)',
                          },
                        }}
                      >
                        {isTimerRunning ? <Pause /> : <PlayArrow />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="停止">
                      <IconButton
                        onClick={stopTimer}
                        color="error"
                        disabled={timerDuration === 0}
                        sx={{
                          background: timerDuration > 0 ? 'linear-gradient(135deg, #f44336, #e53935)' : 'rgba(0,0,0,0.1)',
                          color: 'white',
                          '&:hover': {
                            background: timerDuration > 0 ? 'linear-gradient(135deg, #d32f2f, #c62828)' : 'rgba(0,0,0,0.1)',
                            transform: timerDuration > 0 ? 'scale(1.1)' : 'none',
                          },
                        }}
                      >
                        <Stop />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ) : !skipDuration ? (
                <Slider
                  value={duration}
                  onChange={(e, newValue) => setDuration(newValue)}
                  min={0}
                  max={120}
                  step={1}
                  marks={[
                    { value: 0, label: '0分' },
                    { value: 5, label: '5分' },
                    { value: 30, label: '30分' },
                    { value: 60, label: '1時間' },
                    { value: 120, label: '2時間' }
                  ]}
                  sx={{ 
                    mt: 2,
                    '& .MuiSlider-thumb': {
                      background: 'linear-gradient(135deg, #66BB6A, #42A5F5)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    },
                    '& .MuiSlider-track': {
                      background: 'linear-gradient(90deg, #66BB6A, #42A5F5)',
                    },
                  }}
                />
              ) : null}
            </Grid>

            {/* 日時指定 */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" mb={2}>
                <Schedule sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  記録日時: {useCustomDateTime && customDateTime ? (
                    onlyDate ? new Date(customDateTime + 'T00:00:00').toLocaleDateString('ja-JP') + ' (時刻不明)' :
                    new Date(customDateTime).toLocaleString('ja-JP')
                  ) : '現在時刻を使用'}
                </Typography>
              </Box>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={useCustomDateTime}
                    onChange={(e) => {
                      setUseCustomDateTime(e.target.checked);
                      if (!e.target.checked) {
                        setOnlyDate(false);
                        setCustomDateTime('');
                      }
                    }}
                  />
                }
                label="日時を手動で指定"
                sx={{ mb: 2 }}
              />
              
              {useCustomDateTime && (
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={onlyDate}
                        onChange={(e) => {
                          setOnlyDate(e.target.checked);
                          if (e.target.checked && customDateTime.includes('T')) {
                            // datetime-localの値から日付部分のみを抽出
                            setCustomDateTime(customDateTime.split('T')[0]);
                          }
                        }}
                      />
                    }
                    label="時刻がわからない（日付のみ）"
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    fullWidth
                    type={onlyDate ? "date" : "datetime-local"}
                    label={onlyDate ? "記録日" : "記録日時"}
                    value={customDateTime}
                    onChange={(e) => setCustomDateTime(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Schedule color="primary" />
                        </InputAdornment>
                      ),
                    }}
                    helperText={
                      onlyDate ? 
                      "時刻情報は記録されません（日付のみ）" :
                      "日時を指定しない場合は現在時刻が使用されます"
                    }
                    sx={{ mt: 1 }}
                  />
                </Box>
              )}
            </Grid>

            {/* 気分・感情 */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>
                  <Box display="flex" alignItems="center">
                    <Mood sx={{ mr: 1, fontSize: '1.2rem' }} />
                    気分・感情
                  </Box>
                </InputLabel>
                <Select
                  value={feeling}
                  onChange={(e) => setFeeling(e.target.value)}
                  label="気分・感情"
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                >
                  {FEELING_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      <Box display="flex" alignItems="center">
                        <IconAvatar type="feeling" value={option} size={24} showGradient={false} />
                        <Typography sx={{ ml: 1 }}>{option}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* カスタム気分入力 */}
            {feeling === 'その他' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="気分を入力"
                  value={customFeeling}
                  onChange={(e) => setCustomFeeling(e.target.value)}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Mood color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}

            {/* コンテンツタイプ */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>
                  <Box display="flex" alignItems="center">
                    <Category sx={{ mr: 1, fontSize: '1.2rem' }} />
                    内容タイプ
                  </Box>
                </InputLabel>
                <Select
                  value={contentType}
                  onChange={(e) => setContentType(e.target.value)}
                  label="内容タイプ"
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                >
                  {CONTENT_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      <Box display="flex" alignItems="center">
                        <IconAvatar type="content" value={option} size={24} showGradient={false} />
                        <Typography sx={{ ml: 1 }}>{option}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* タグ */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="タグ（カンマ区切り）"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="リラックス, 音楽, 瞑想"
                helperText="複数のタグはカンマで区切ってください"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Tag color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* メモ */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="メモ（任意）"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="今日の体験について自由に記録..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Note color="primary" sx={{ alignSelf: 'flex-start', mt: 1 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button 
          onClick={handleClose}
          sx={{
            borderRadius: '12px',
            px: 3,
            py: 1.5,
            fontWeight: 600,
          }}
        >
          キャンセル
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !feeling || !contentType}
          sx={{ 
            borderRadius: '12px',
            px: 3,
            py: 1.5,
            background: 'linear-gradient(135deg, #66BB6A, #42A5F5)',
            fontWeight: 600,
            '&:hover': {
              background: 'linear-gradient(135deg, #4CAF50, #1976D2)',
              transform: 'translateY(-1px)',
            },
          }}
          startIcon={<Save />}
        >
          {loading ? '更新中...' : '記録を更新'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}